/**
 * Convert.com analytics JavaScript API.
 *
 * Taken from {@link https://support.convert.com/hc/en-us/articles/implementing-converts-full-stack-javascript-sdk-real-world-examples}
 */
export interface Convert {
  triggerConversion: (conversionId: string) => Promise<void>;
}

export type ConvertConversionData = {
  conversionId: string;
};

declare global {
  interface Window {
    convert?: Convert;
  }
}

export const convert = {
  /**
   * Trigger a conversion in Convert.com.
   */
  triggerConversion: async (conversionId: string): Promise<void> => {
    type ConvertQueueWindow = Window & {
      _conv_q?: Array<['triggerConversion', string]>;
    };

    const windowWithConvertQueue = window as ConvertQueueWindow;
    windowWithConvertQueue._conv_q = windowWithConvertQueue._conv_q || [];
    windowWithConvertQueue._conv_q.push(['triggerConversion', conversionId]);

    return Promise.resolve();
  },
};

let eventQueue: ConvertConversionData[] = [];

// Watch for when convert loads. When it's ready, use it and also track queued events.
let retry = 0;
let timer: ReturnType<typeof setInterval> | undefined = undefined;

/**
 * If Convert is loaded, track a conversion event. Otherwise, add it
 * to a queue of events to be tracked once Convert is loaded.
 */
export function trackConversion(event: ConvertConversionData): void {
  if (window.convert) {
    convert.triggerConversion(event.conversionId);
    return;
  }

  eventQueue.push({ conversionId: event.conversionId });
  // The first time we try to track an event and Convert doesn't exist,
  // start looking for when Convert is loaded
  if (typeof timer === 'undefined') {
    timer = setInterval(() => {
      if (window?.convert) {
        trackEventsInQueue(window.convert);
        clearInterval(timer);
        return;
      }
      retry++;
      if (retry >= 5) {
        clearInterval(timer);
      }
    }, 1000);
  }
}

/**
 * Track all events in the event queue, then clear it.
 */
const trackEventsInQueue = (convert: Convert) => {
  for (const { conversionId } of eventQueue) {
    convert.triggerConversion(conversionId);
  }

  // Clear queue after tracking everything
  eventQueue = [];
};
